/**
 * V2QsMultiTextNested 컴포넌트 Props 인터페이스
 * V1 QsInputMultiTextNested의 V2 버전 (순수 UI만)
 *
 * 중첩 옵션이 있는 다중 텍스트 입력 컴포넌트
 */

/**
 * 중첩 질문 옵션
 */
export interface V2QsNestedOption {
  /**
   * 옵션 키
   */
  key: string;

  /**
   * 표시할 라벨
   */
  label: string;

  /**
   * 노트 입력 필요 여부
   */
  requireNote?: boolean;

  /**
   * 상호 배타적 옵션 여부
   */
  mutuallyExclusive?: boolean;
}

/**
 * 중첩 질문
 */
export interface V2QsNestedQuestion {
  /**
   * 질문 텍스트
   */
  question: string;

  /**
   * 옵션 목록
   */
  options: V2QsNestedOption[];
}

/**
 * 태그 데이터
 */
export interface V2QsMultiTextNestedTag {
  /**
   * 태그 ID
   */
  id: string;

  /**
   * 태그 텍스트
   */
  text: string;

  /**
   * 선택된 옵션 키 배열
   */
  selectedOptions: string[];

  /**
   * 옵션별 노트
   */
  notesForKeys?: Record<string, string>;
}

export interface V2QsMultiTextNestedProps {
  /**
   * 태그 목록
   */
  value?: V2QsMultiTextNestedTag[];

  /**
   * 값 변경 시 콜백
   */
  onChange?: (value: V2QsMultiTextNestedTag[]) => void;

  /**
   * 중첩 질문 목록
   */
  nestedQuestions?: V2QsNestedQuestion[];

  /**
   * 현재 열린 태그의 인덱스 (모달 표시용)
   */
  openTagIndex?: number | null;

  /**
   * 모달 열기 콜백
   */
  onOpenModal?: (tagIndex: number) => void;

  /**
   * 모달 닫기 콜백
   */
  onCloseModal?: () => void;

  /**
   * 최소 태그 수
   */
  min?: number;

  /**
   * 최대 태그 수
   */
  max?: number;

  /**
   * 디스플레이 모드 (수정 불가)
   */
  isDisplayMode?: boolean;

  /**
   * 비활성화 여부
   */
  disabled?: boolean;

  /**
   * 읽기 전용 여부
   */
  readOnly?: boolean;

  /**
   * 에러 상태
   */
  hasError?: boolean;

  /**
   * 에러 메시지
   */
  errorMessage?: string;

  // i18n 텍스트 Props
  /**
   * 플레이스홀더
   */
  placeholder?: string;

  /**
   * 추가 버튼 텍스트
   */
  addButtonText?: string;

  /**
   * 선택 버튼 텍스트
   */
  selectButtonText?: string;

  /**
   * 닫기 버튼 텍스트
   */
  closeButtonText?: string;

  /**
   * 최소 개수 미달 에러 메시지 템플릿
   */
  minErrorTemplate?: string;

  /**
   * 최대 개수 초과 에러 메시지 템플릿
   */
  maxErrorTemplate?: string;

  /**
   * 추가 CSS 클래스
   */
  className?: string;
}

export const defaultV2QsMultiTextNestedProps: Partial<V2QsMultiTextNestedProps> = {
  isDisplayMode: false,
  disabled: false,
  readOnly: false,
  hasError: false,
  placeholder: 'Type and add...',
  addButtonText: 'ADD',
  selectButtonText: 'SELECT',
  closeButtonText: 'Close',
  minErrorTemplate: 'Please enter at least {min} item(s).',
  maxErrorTemplate: 'You can enter up to {max} item(s) only.',
};
